import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { mediaList, regionOptions, yearOptions } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const typeFilters = ["全部", "电影", "电视剧", "综艺"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [type, setType] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useDocumentMeta({
    title: query ? `“${query}”的搜索结果 - 蘑菇视频` : "搜索影视 - 蘑菇视频",
    description: `在蘑菇视频搜索“${query}”相关的电影、电视剧和综艺内容，快速找到你想看的影视作品。`,
    keywords: `${query},蘑菇视频,影视搜索,在线观看`,
  });

  const results = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return [];
    return mediaList.filter((m) => {
      const haystack = [m.title, m.director, ...m.actors, m.genre, m.region, ...m.tags, m.type]
        .join(" ")
        .toLowerCase();
      const matchKw = haystack.includes(kw);
      const matchType = type === "全部" || m.type === type;
      const matchRegion = region === "全部" || m.region === region;
      const matchYear = year === "全部" || String(m.year) === year;
      return matchKw && matchType && matchRegion && matchYear;
    });
  }, [query, type, region, year]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
    setType("全部");
    setRegion("全部");
    setYear("全部");
  };

  const resetFilters = () => {
    setType("全部");
    setRegion("全部");
    setYear("全部");
  };

  const chip = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer ${
      active ? "bg-primary-500 font-semibold text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
    }`;

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb items={[{ label: "搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视搜索</h1>
          <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row" role="search">
            <div className="relative w-full sm:max-w-xl">
              <span className="absolute left-4 flex h-4 w-4 items-center justify-center top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-search-line text-base" aria-hidden="true"></i>
              </span>
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、演员或导演…"
                aria-label="搜索影视内容"
                className="w-full rounded-full border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-search-line" aria-hidden="true"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        {query ? (
          <>
            <p className="text-sm text-foreground-600">
              关键词 <span className="font-semibold text-primary-600">“{query}”</span> 共找到{" "}
              <span className="font-semibold text-primary-600">{results.length}</span> 个结果
            </p>

            <div className="mt-4 space-y-2 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-start gap-3">
                <span className="w-12 shrink-0 pt-1.5 text-xs font-medium text-foreground-500">类型</span>
                <div className="flex flex-wrap gap-2">
                  {typeFilters.map((t) => (
                    <button key={t} type="button" onClick={() => setType(t)} className={chip(type === t)}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-12 shrink-0 pt-1.5 text-xs font-medium text-foreground-500">地区</span>
                <div className="flex flex-wrap gap-2">
                  {regionOptions.map((r) => (
                    <button key={r} type="button" onClick={() => setRegion(r)} className={chip(region === r)}>{r}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-12 shrink-0 pt-1.5 text-xs font-medium text-foreground-500">年份</span>
                <div className="flex flex-wrap gap-2">
                  {yearOptions.map((y) => (
                    <button key={y} type="button" onClick={() => setYear(y)} className={chip(year === y)}>{y}</button>
                  ))}
                </div>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {results.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
                  <i className="ri-search-eye-line" aria-hidden="true"></i>
                </span>
                <p className="mt-5 text-lg font-semibold text-foreground-900">未找到匹配内容</p>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  没有找到与“{query}”相关的结果。你可以尝试更换关键词，或清除筛选条件后重新搜索。
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-refresh-line" aria-hidden="true"></i>
                    清除筛选
                  </button>
                  <Link
                    to="/"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-home-4-line" aria-hidden="true"></i>
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <p className="mt-5 text-lg font-semibold text-foreground-900">开始探索你想看的影视</p>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              输入影视名称、演员或导演，即可在蘑菇视频海量内容中快速查找。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {["星河彼岸", "迷雾之城", "美食奇遇记", "龙腾九霄"].map((kw) => (
                <Link
                  key={kw}
                  to={`/search?q=${encodeURIComponent(kw)}`}
                  className="rounded-full border border-background-200 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                >
                  {kw}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}